import { useRef, useState } from "react";
import { Download } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { notify } from "@/components/common/Notification";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import UsLogo from "../../assets/us-logo.png";

const BLUE = "#23245b";
const GREY = "#656565";
const BLACK = "#000000";
const LINE = "#cfcece";

export type ServiceConfirmationCharge = {
  item: string;
  description: string;
  estimatedYearlyCost: string;
};

export type ServiceConfirmationData = {
  preparedFor: string;
  dateIssued: string;
  client: {
    name: string;
    email: string;
    company: string;
    phone: string;
  };
  project: {
    name: string;
    engagementType: string;
    billingModel: string;
    category: string;
    startDate: string;
    currency: string;
  };
  charges: ServiceConfirmationCharge[];
  contact: {
    manager: string;
    email: string;
    phone: string;
  };
  signatory: {
    name: string;
    designation: string;
    date: string;
  };
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: ServiceConfirmationData;
};

const AUTHORIZATION_POINTS = [
  "All third-party and infrastructure costs (domain, hosting, SSL, WhatsApp/SMS/Meta API, payment gateway, etc.) are to be borne directly by the client. Universal Software can assist with setup and onboarding on request.",
  "Travelling expenses, if any, are to be borne by the client.",
  "Complete scope of work, deliverables, legal terms and conditions are governed by the Proposal and Master Service Agreement, provided as a separate document.",
];

/** html2canvas ignores default line-box leading, so spacing is set explicitly. */
const LINE_HEIGHT = 1.6;
const ROW_TEXT_STYLE = {
  margin: 0,
  lineHeight: LINE_HEIGHT,
} as const;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        color: BLUE,
        borderBottom: `1px solid ${BLUE}`,
        paddingBottom: 6,
        margin: 0,
        fontWeight: 700,
        lineHeight: LINE_HEIGHT,
      }}
    >
      {children}
    </p>
  );
}

function DetailRow({
  label,
  value,
  first,
}: {
  label: string;
  value: string;
  first?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        borderBottom: `1px solid ${LINE}`,
        marginTop: first ? 0 : 8,
        paddingBottom: 5,
      }}
    >
      <p style={{ ...ROW_TEXT_STYLE, color: GREY, width: "40%" }}>{label}</p>
      <p
        style={{
          ...ROW_TEXT_STYLE,
          color: BLACK,
          width: "60%",
          fontWeight: 700,
        }}
      >
        {value || "--"}
      </p>
    </div>
  );
}

function SignatureRow({
  label,
  value,
  first,
}: {
  label: string;
  value: string;
  first?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        borderBottom: `1px solid ${LINE}`,
        marginTop: first ? 8 : 6,
        paddingBottom: 5,
      }}
    >
      <p style={{ ...ROW_TEXT_STYLE, color: GREY, width: "40%" }}>{label}</p>
      <p
        style={{
          ...ROW_TEXT_STYLE,
          color: BLACK,
          width: "60%",
          fontWeight: 700,
          textAlign: "end",
        }}
      >
        {value}
      </p>
    </div>
  );
}

const ProjectServiceConfirmationModal = ({
  open,
  onOpenChange,
  data,
}: Props) => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    const node = documentRef.current;
    if (!node) {
      return;
    }

    try {
      setIsDownloading(true);
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });

      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "p" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;
      const imageData = canvas.toDataURL("image/png");

      // Always keep the document on a single page: shrink to fit when the
      // charges table makes it taller than one A4 sheet.
      const scale = Math.min(
        usableWidth / canvas.width,
        usableHeight / canvas.height
      );
      const imageWidth = canvas.width * scale;
      const imageHeight = canvas.height * scale;
      const offsetX = margin + (usableWidth - imageWidth) / 2;

      pdf.addImage(imageData, "PNG", offsetX, margin, imageWidth, imageHeight);

      const fileName =
        `${data.project.name || "project"}-service-confirmation.pdf`
          .replace(/[\\/:*?"<>|]+/g, "-")
          .replace(/\s+/g, "-")
          .toLowerCase();
      pdf.save(fileName);
    } catch {
      notify.error("Failed to download PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[95vw] max-w-[900px] gap-0 overflow-hidden bg-white p-0">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-[#111827]">
              Project Initiation &amp; Service Confirmation
            </h2>
            <p className="text-xs text-[#627384]">
              Preview of the client onboarding document
            </p>
          </div>
          <div className="flex items-center gap-2 pr-8">
            <Button
              type="button"
              onClick={handleDownloadPdf}
              disabled={isDownloading}
              className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
            >
              <Download className="h-4 w-4" />
              {isDownloading ? "Downloading..." : "Download PDF"}
            </Button>
          </div>
        </div>

        <div className="max-h-[80vh] overflow-auto bg-gray-100 p-4">
          <div
            ref={documentRef}
            style={{
              width: 800,
              margin: "auto",
              padding: 24,
              backgroundColor: "#ffffff",
              fontFamily: "'Noto Sans', sans-serif",
              fontWeight: 500,
              color: BLACK,
              lineHeight: LINE_HEIGHT,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: `2px solid ${BLUE}`,
                paddingBottom: 8,
              }}
            >
              <img
                src={UsLogo}
                alt="Universal Software"
                style={{ width: 96 }}
              />
              <div style={{ color: BLUE, fontSize: 20 }}>
                <p style={{ ...ROW_TEXT_STYLE, fontWeight: 700 }}>
                  Project initiation &amp; service confirmation
                </p>
                <p
                  style={{
                    ...ROW_TEXT_STYLE,
                    fontSize: 16,
                    textAlign: "end",
                    color: GREY,
                  }}
                >
                  Client onboarding document
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                marginTop: 6,
                fontSize: 13,
                color: BLACK,
                borderBottom: `1px solid ${GREY}`,
                paddingBottom: 6,
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <p style={ROW_TEXT_STYLE}>Prepared for :</p>
                <p style={{ ...ROW_TEXT_STYLE, fontWeight: 700 }}>
                  {data.preparedFor || "--"}
                </p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <p style={ROW_TEXT_STYLE}>Date Issued :</p>
                <p style={{ ...ROW_TEXT_STYLE, fontWeight: 700 }}>
                  {data.dateIssued}
                </p>
              </div>
            </div>

            <div style={{ marginTop: 25 }}>
              <SectionTitle>Client Details</SectionTitle>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginTop: 4,
                  fontSize: 13,
                }}
              >
                <div style={{ width: "50%" }}>
                  <DetailRow
                    first
                    label="Client Name"
                    value={data.client.name}
                  />
                  <DetailRow label="Email" value={data.client.email} />
                </div>
                <div style={{ width: "50%" }}>
                  <DetailRow
                    first
                    label="Company"
                    value={data.client.company}
                  />
                  <DetailRow label="Phone" value={data.client.phone} />
                </div>
              </div>
            </div>

            <div style={{ marginTop: 25 }}>
              <SectionTitle>Project information</SectionTitle>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginTop: 4,
                  fontSize: 13,
                }}
              >
                <div style={{ width: "50%" }}>
                  <DetailRow
                    first
                    label="Project name"
                    value={data.project.name}
                  />
                  <DetailRow
                    label="Engagement type"
                    value={data.project.engagementType}
                  />
                  <DetailRow
                    label="Billing model"
                    value={data.project.billingModel}
                  />
                </div>
                <div style={{ width: "50%" }}>
                  <DetailRow
                    first
                    label="Project category"
                    value={data.project.category}
                  />
                  <DetailRow
                    label="Start date"
                    value={data.project.startDate}
                  />
                  <DetailRow label="Currency" value={data.project.currency} />
                </div>
              </div>
            </div>

            <div style={{ marginTop: 25 }}>
              <SectionTitle>
                Third-party &amp; infrastructure charges
              </SectionTitle>
              <div style={{ marginTop: 6, color: GREY, fontSize: 13 }}>
                <p style={{ ...ROW_TEXT_STYLE, fontStyle: "italic" }}>
                  Indicative rates, billed to client at actuals – see
                  authorization note below
                </p>
              </div>
            </div>

            <div style={{ marginTop: 25, fontSize: 13, color: BLACK }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead
                  style={{
                    backgroundColor: "#eae9e9",
                    borderBottom: "2px solid #9e9e9e",
                    borderTop: "2px solid #9e9e9e",
                  }}
                >
                  <tr>
                    <th
                      style={{
                        width: "20%",
                        padding: "9px 10px",
                        fontSize: 13,
                        textAlign: "left",
                        lineHeight: LINE_HEIGHT,
                      }}
                    >
                      Item
                    </th>
                    <th
                      style={{
                        width: "35%",
                        padding: "9px 10px",
                        fontSize: 13,
                        textAlign: "left",
                        lineHeight: LINE_HEIGHT,
                      }}
                    >
                      Description
                    </th>
                    <th
                      style={{
                        width: "45%",
                        padding: "9px 10px",
                        fontSize: 13,
                        textAlign: "left",
                        lineHeight: LINE_HEIGHT,
                      }}
                    >
                      Estimated yearly cost (INR)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.charges.map(charge => (
                    <tr
                      key={`${charge.item}`}
                      style={{ borderBottom: `1px solid ${LINE}` }}
                    >
                      <td
                        style={{
                          color: BLACK,
                          fontWeight: 700,
                          padding: "7px 10px",
                          verticalAlign: "top",
                          fontSize: 13,
                          lineHeight: LINE_HEIGHT,
                        }}
                      >
                        {charge.item}
                      </td>
                      <td
                        style={{
                          color: GREY,
                          padding: "7px 10px",
                          verticalAlign: "top",
                          fontSize: 13,
                          lineHeight: LINE_HEIGHT,
                        }}
                      >
                        {charge.description || "--"}
                      </td>
                      <td
                        style={{
                          color: GREY,
                          padding: "7px 10px",
                          verticalAlign: "top",
                          fontSize: 13,
                          lineHeight: LINE_HEIGHT,
                        }}
                      >
                        {charge.estimatedYearlyCost || "--"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 25 }}>
              <SectionTitle>Project contact</SectionTitle>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginTop: 4,
                  fontSize: 13,
                }}
              >
                <div style={{ width: "50%" }}>
                  <DetailRow
                    first
                    label="Project manager"
                    value={data.contact.manager}
                  />
                  <DetailRow label="Email" value={data.contact.email} />
                </div>
                <div style={{ width: "50%" }}>
                  <DetailRow first label="Phone" value={data.contact.phone} />
                </div>
              </div>
            </div>

            <div style={{ marginTop: 25 }}>
              <SectionTitle>Client authorization</SectionTitle>
              <div
                style={{
                  marginTop: 10,
                  color: GREY,
                  fontSize: 13,
                  backgroundColor: "#f1f1f1",
                  padding: "8px 10px",
                  border: "1px solid #9e9e9e",
                  borderRadius: 5,
                }}
              >
                <p style={ROW_TEXT_STYLE}>
                  We confirm the above project information and authorize
                  Universal Software to initiate the project, subject to the
                  following:
                </p>
                <div style={{ marginTop: 6 }}>
                  {AUTHORIZATION_POINTS.map(point => (
                    <div
                      key={point}
                      style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 4,
                      }}
                    >
                      <span style={{ ...ROW_TEXT_STYLE, flexShrink: 0 }}>
                        •
                      </span>
                      <p style={ROW_TEXT_STYLE}>{point}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div
                style={{
                  marginTop: 6,
                  marginBottom: 10,
                  color: GREY,
                  fontSize: 13,
                }}
              >
                <p style={ROW_TEXT_STYLE}>
                  This document confirms project commencement and does not
                  itself constitute the legal agreement between the parties.
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 16,
                marginTop: 25,
                fontSize: 13,
                borderBottom: "2px solid #eae9e9",
                paddingBottom: 7,
              }}
            >
              <div style={{ width: "50%" }}>
                <p style={{ ...ROW_TEXT_STYLE, color: BLUE, fontWeight: 700 }}>
                  For Universal Software
                </p>
                <SignatureRow
                  first
                  label="Signatory"
                  value={data.signatory.name}
                />
                <SignatureRow
                  label="Designation"
                  value={data.signatory.designation}
                />
                <SignatureRow label="Date" value={data.signatory.date} />
              </div>
              <div style={{ width: "50%" }}>
                <p style={{ ...ROW_TEXT_STYLE, color: BLUE, fontWeight: 700 }}>
                  For {data.client.company || data.client.name || "Client"}
                </p>
                <SignatureRow first label="Signatory" value="" />
                <SignatureRow label="Company stamp" value="" />
                <SignatureRow label="Date" value="" />
              </div>
            </div>

            <div style={{ marginTop: 15, color: GREY, fontSize: 13 }}>
              <p style={ROW_TEXT_STYLE}>
                <span style={{ color: BLUE, fontWeight: 700 }}>
                  Universal Software
                </span>{" "}
                — Creative Technology Solutions
              </p>
              <p style={ROW_TEXT_STYLE}>
                This document confirms project commencement only. The Proposal,
                MSA and MSP govern the complete scope, deliverables, legal and
                commercial terms and shall prevail in case of any conflict.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectServiceConfirmationModal;
